import React, { useCallback, useEffect, useRef } from "react";
import {
  useCanvasPos,
  useCanvasStore,
} from "@/components/Canvas/stores/canvasStore";
import { cn } from "@/lib/utils";
import { useActiveCanvas } from "@/utils/utils";
import { getUnzoomedPixelValue } from "../Canvas/utils/utils";
import ElementOutline, {
  COMPONENT_OUTLINE_COLOR,
  type ElementOutlineRef,
  OutlineType,
} from "./ElementOutline";
import type { CanvasItemRef } from "./SimpleCanvas";

const CORNER_HANDLE_SIZE = 10; // Size of the visible corner squares
const CORNER_HIT_AREA_SIZE = 14; // Size of the clickable area for corners
const EDGE_HIT_AREA_WIDTH = 4; // Width of the invisible edge hit areas

// Handle positioning and resize direction helpers
const getHandleDirections = (handleType: ResizeHandleType) => {
  const isEast = handleType.includes("e");
  const isWest = handleType.includes("w");
  const isSouth = handleType.includes("s");
  const isNorth = handleType.includes("n");

  const isCorner = (isEast || isWest) && (isNorth || isSouth);

  return {
    isHorizontalEdge: (isEast || isWest) && !isCorner, // Pure horizontal edge handles (e/w only)
    isVerticalEdge: (isNorth || isSouth) && !isCorner, // Pure vertical edge handles (n/s only)
    isCorner,
    isEast,
    isWest,
    isSouth,
    isNorth,
  };
};

// Cursor mapping
const cursors: Record<ResizeHandleType, string> = {
  n: "ns-resize",
  s: "ns-resize",
  e: "ew-resize",
  w: "ew-resize",
  nw: "nwse-resize",
  se: "nwse-resize",
  ne: "nesw-resize",
  sw: "nesw-resize",
};

const getHandleBounds = (
  handleType: ResizeHandleType,
  unzoomMultiplier: number,
  itemRect: Omit<ItemRect, "tagName" | "isComponent">,
) => {
  const { x, y, width, height } = itemRect;

  const {
    isHorizontalEdge,
    isVerticalEdge,
    isCorner,
    isEast,
    isWest,
    isSouth,
    isNorth,
  } = getHandleDirections(handleType);
  const hitAreaSize = isCorner ? CORNER_HIT_AREA_SIZE : EDGE_HIT_AREA_WIDTH;

  const left = isWest
    ? x - (hitAreaSize / 2) * unzoomMultiplier
    : isEast
      ? x + width - (hitAreaSize / 2) * unzoomMultiplier
      : x;

  const top = isNorth
    ? y - (hitAreaSize / 2) * unzoomMultiplier
    : isSouth
      ? y + height - (hitAreaSize / 2) * unzoomMultiplier
      : y;

  return {
    left: `${left}px`,
    top: `${top}px`,
    width: isVerticalEdge
      ? `${width}px`
      : `${hitAreaSize * unzoomMultiplier}px`,
    height: isHorizontalEdge
      ? `${height}px`
      : `${hitAreaSize * unzoomMultiplier}px`,
  };
};

// Geometry helpers
const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const getCenter = (bounds: {
  x: number;
  y: number;
  width: number;
  height: number;
}) => ({
  x: bounds.x + bounds.width / 2,
  y: bounds.y + bounds.height / 2,
});

interface ItemRect {
  x: number;
  y: number;
  width: number;
  height: number;

  // For the outline style
  tagName?: string;
  isComponent?: boolean;
}

type ResizeHandleType = "nw" | "ne" | "sw" | "se" | "n" | "e" | "s" | "w";

interface ResizeState {
  isResizing: boolean;
  handleType: ResizeHandleType | null;
  originalBounds: { x: number; y: number; width: number; height: number };
  startMouseX: number;
  startMouseY: number;
  originalAspectRatio: number;
}

interface ResizableOutlineRef {
  startResize: (e: React.MouseEvent, handleType: ResizeHandleType) => void;
  applyFullResize: (rect: Partial<ItemRect>) => void;
  applyInstantResize: (rect: Partial<ItemRect>) => void; // Notifies parent of changes
  instantUpdateWithRect: (rect: Partial<ItemRect>) => void; // Only updates the outline
  instantUpdateZIndex: (zIndex: number) => void;
}

interface ResizableOutlineProps<T extends ItemRect> {
  itemRect: T; // Item rectangle in canvas coordinates (non-zoomed)

  // Element that is being resized
  // The resize handles will resize the object itself directly
  elementsResizing?: (HTMLElement | CanvasItemRef)[];

  onItemChange: (itemRect: T) => void;
  onResizeStart?: () => void;
  onResizeEnd?: (itemRect: T) => void;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;

  clientToCanvas?: (
    clientX: number,
    clientY: number,
  ) => { x: number; y: number } | undefined;

  // If set to false, it's because the outline cannot be moved,
  // so only change the size
  canMove?: boolean;

  hidden?: boolean;
  notInteractable?: boolean;

  iframeRef?: React.RefObject<HTMLIFrameElement>;

  droppingUI?: boolean;
}

const ResizableOutline = React.forwardRef<
  ResizableOutlineRef,
  ResizableOutlineProps<any>
>(
  (
    {
      itemRect,
      elementsResizing,
      onItemChange,
      onResizeStart,
      onResizeEnd,
      minWidth = 20,
      minHeight = 20,
      maxWidth = Infinity,
      maxHeight = Infinity,
      clientToCanvas,
      canMove = true,
      hidden = false,
      notInteractable = false,
      iframeRef,
      droppingUI = false,
    },
    ref,
  ) => {
    const handleRefs = useRef<
      Map<ResizeHandleType | "outline", HTMLDivElement | ElementOutlineRef>
    >(new Map());
    const resizeStateRef = useRef<ResizeState>({
      isResizing: false,
      handleType: null,
      originalBounds: { x: 0, y: 0, width: 0, height: 0 },
      startMouseX: 0,
      startMouseY: 0,
      originalAspectRatio: 1,
    });

    const elementsResizingRef = useRef<
      (HTMLElement | CanvasItemRef)[] | undefined
    >(elementsResizing);
    useEffect(() => {
      elementsResizingRef.current = elementsResizing;
    }, [elementsResizing]);

    const outerDivRef = useRef<HTMLDivElement>(null);
    const unzoomMultiplierRef = useRef(1);
    const activeCanvas = useActiveCanvas();

    // Ref to hold the CSS variable update function so it can be called from multiple places
    const updateCssVariablesRef = useRef<(() => void) | null>(null);

    // Subscribe to realtime zoom and update CSS variables imperatively (no React re-renders)
    useEffect(() => {
      const updateCssVariables = () => {
        const canvasId = activeCanvas?.id;
        if (!canvasId || !outerDivRef.current) return;

        const zoomPerc =
          useCanvasStore.getState().canvasIdToPosRealtime[canvasId]?.zoomPerc ||
          1;
        const multiplier = 1 / zoomPerc;
        unzoomMultiplierRef.current = multiplier;
        outerDivRef.current.style.setProperty(
          "--unzoom-multiplier",
          String(multiplier),
        );

        // Calculate handle visibility: hide when storyboard is too small on screen
        // Screen size = canvas size * zoom, handle size is constant (CORNER_HANDLE_SIZE)
        const rect = pendingUpdateItemRect.current;
        const smallerDimension = Math.min(rect.width, rect.height);
        const storyboardScreenSize = smallerDimension * zoomPerc;
        const shouldShowHandles =
          storyboardScreenSize >= 1.5 * CORNER_HANDLE_SIZE;
        outerDivRef.current.style.setProperty(
          "--handle-display",
          shouldShowHandles ? "block" : "none",
        );
      };

      updateCssVariablesRef.current = updateCssVariables;

      // Initial update
      updateCssVariables();

      // Subscribe to store changes
      const unsubscribe = useCanvasStore.subscribe(updateCssVariables);
      return () => unsubscribe();
    }, [activeCanvas?.id]);

    const resizeRafId = useRef<number | null>(null);
    const rafScheduled = useRef(false);
    const updateRafId = useRef<number | null>(null);

    // Single accumulator for all pending updates
    const pendingUpdateItemRect = useRef<
      Omit<ItemRect, "tagName" | "isComponent">
    >({
      x: itemRect.x || 0,
      y: itemRect.y || 0,
      width: itemRect.width || 0,
      height: itemRect.height || 0,
    });
    const appliedItemRectRef = useRef<
      Omit<ItemRect, "tagName" | "isComponent">
    >({
      x: itemRect.x || 0,
      y: itemRect.y || 0,
      width: itemRect.width || 0,
      height: itemRect.height || 0,
    });

    const lockedSizeRef = useRef(false);

    // Main RAF update function
    const applyPendingUpdates = useCallback(() => {
      // Resize the element itself
      if (
        resizeStateRef.current?.isResizing &&
        resizeStateRef.current?.handleType
      ) {
        const { isHorizontalEdge, isVerticalEdge, isCorner } =
          getHandleDirections(resizeStateRef.current?.handleType);

        elementsResizingRef.current?.forEach((element) => {
          if ((element as CanvasItemRef)?.onResizeInstantUpdate) {
            (element as CanvasItemRef).onResizeInstantUpdate?.({
              x: pendingUpdateItemRect.current.x,
              y: pendingUpdateItemRect.current.y,
              width: pendingUpdateItemRect.current.width,
              height: pendingUpdateItemRect.current.height,
            });
          } else {
            const elementHTMLElement = element as HTMLElement;
            if (canMove) {
              elementHTMLElement.style.transform = `translate3d(${pendingUpdateItemRect.current.x}px, ${pendingUpdateItemRect.current.y}px, 0)`;
            }

            if (isHorizontalEdge || isCorner) {
              const currentWidth = elementHTMLElement.style.width;
              if (
                !elementHTMLElement.hasAttribute("data-original-width") &&
                elementHTMLElement.id !== "simple-canvas-item-container"
              ) {
                elementHTMLElement.setAttribute(
                  "data-original-width",
                  currentWidth || "",
                );
              }
              elementHTMLElement.style.width = `${pendingUpdateItemRect.current.width}px`;
            }

            if (isVerticalEdge || isCorner) {
              const currentHeight = elementHTMLElement.style.height;
              if (
                !elementHTMLElement.hasAttribute("data-original-height") &&
                elementHTMLElement.id !== "simple-canvas-item-container"
              ) {
                elementHTMLElement.setAttribute(
                  "data-original-height",
                  currentHeight || "",
                );
              }
              elementHTMLElement.style.height = `${pendingUpdateItemRect.current.height}px`;
            }

            // Special case - check if the element has moved because of the resize
            // Inspect its x & y values to see where it is now
            if (!canMove) {
              const elementRect = (
                element as HTMLElement
              ).getBoundingClientRect();
              if (
                elementRect.x !== pendingUpdateItemRect.current.x ||
                elementRect.y !== pendingUpdateItemRect.current.y
              ) {
                pendingUpdateItemRect.current.x = elementRect.x;
                pendingUpdateItemRect.current.y = elementRect.y;
              }
            }
          }
        });
      }

      // Update the outline size
      const outline = handleRefs.current.get("outline");
      if (outline) {
        (outline as ElementOutlineRef).instantUpdateWithRect({
          x: pendingUpdateItemRect.current.x,
          y: pendingUpdateItemRect.current.y,
          width: pendingUpdateItemRect.current.width,
          height: pendingUpdateItemRect.current.height,
        });
      }

      const handles: ResizeHandleType[] = [
        "n",
        "e",
        "s",
        "w",
        "nw",
        "ne",
        "sw",
        "se",
      ];

      handles.forEach((handleType) => {
        const handle = handleRefs.current.get(handleType);
        if (!handle) return;

        const { left, top, width, height } = getHandleBounds(
          handleType,
          unzoomMultiplierRef.current,
          pendingUpdateItemRect.current,
        );

        (handle as HTMLDivElement).style.left = left;
        (handle as HTMLDivElement).style.top = top;
        (handle as HTMLDivElement).style.width = width;
        (handle as HTMLDivElement).style.height = height;
      });

      appliedItemRectRef.current = { ...pendingUpdateItemRect.current };
      updateRafId.current = null;
    }, [canMove, unzoomMultiplierRef]);

    // ================================================================================
    // RESIZE LOGIC - Handles dragging + shift/alt for locked aspect ratio / center
    // ================================================================================

    const calculateNewBounds = useCallback(
      (
        handleType: ResizeHandleType,
        originalBounds: { x: number; y: number; width: number; height: number },
        deltaX: number,
        deltaY: number,
        maintainAspectRatio: boolean = false,
        originalAspectRatio: number = 1,
        symmetricalResize: boolean = false,
      ) => {
        const {
          isHorizontalEdge,
          isVerticalEdge,
          isCorner,
          isEast,
          isWest,
          isSouth,
          isNorth,
        } = getHandleDirections(handleType);
        const originalCenter = getCenter(originalBounds);

        let newX = originalBounds.x;
        let newY = originalBounds.y;
        let newWidth = originalBounds.width;
        let newHeight = originalBounds.height;

        // Step 1: Affix the dragged edge/corner to cursor position
        // Affix horizontal dimension to cursor if this handle affects width
        if (isEast || isWest) {
          if (isEast) {
            newWidth = originalBounds.width + deltaX;
          } else if (isWest) {
            newWidth = originalBounds.width - deltaX;
            newX = originalBounds.x + deltaX;
          }
        }
        // Affix vertical dimension to cursor if this handle affects height
        if (isSouth || isNorth) {
          if (isSouth) {
            newHeight = originalBounds.height + deltaY;
          } else if (isNorth) {
            newHeight = originalBounds.height - deltaY;
            newY = originalBounds.y + deltaY;
          }
        }

        // Handle the special case of both aspect ratio and symmetrical resize
        if (maintainAspectRatio && symmetricalResize) {
          // Determine growth (signed) for each axis from the dragged handle.
          const growthX = isEast ? deltaX : isWest ? -deltaX : 0;
          const growthY = isSouth ? deltaY : isNorth ? -deltaY : 0;

          // Apply mirrored growth to get the candidate size.
          let candidateWidth = originalBounds.width + growthX * 2;
          let candidateHeight = originalBounds.height + growthY * 2;

          // Ensure we never go below zero – this avoids division by zero later
          candidateWidth = Math.max(1, candidateWidth);
          candidateHeight = Math.max(1, candidateHeight);

          if (isCorner) {
            // Corner handles affect both axes – pick the dominant axis and
            // scale the other to maintain the original aspect ratio while
            // keeping the affixed corner under the cursor.
            const currentRatio = candidateWidth / candidateHeight;
            if (currentRatio > originalAspectRatio) {
              // Too wide – grow/shrink height symmetrically
              candidateHeight = candidateWidth / originalAspectRatio;
            } else {
              // Too tall – grow/shrink width symmetrically
              candidateWidth = candidateHeight * originalAspectRatio;
            }
          } else if (isHorizontalEdge) {
            // Horizontal edge (e / w) – width is dictated by the cursor,
            // adjust height symmetrically to restore aspect ratio.
            candidateHeight = candidateWidth / originalAspectRatio;
          } else if (isVerticalEdge) {
            // Vertical edge (n / s) – height is dictated by the cursor,
            // adjust width symmetrically to restore aspect ratio.
            candidateWidth = candidateHeight * originalAspectRatio;
          }

          // Apply size constraints while preserving aspect ratio
          let constrainedWidth = clamp(candidateWidth, minWidth, maxWidth);
          let constrainedHeight = clamp(candidateHeight, minHeight, maxHeight);

          // After independent clamping the ratio might be off – fix it by
          // clamping again using whichever dimension actually changed.
          const postClampRatio = constrainedWidth / constrainedHeight;
          if (Math.abs(postClampRatio - originalAspectRatio) > 0.0001) {
            if (postClampRatio > originalAspectRatio) {
              // Width too large relative to height – recompute width
              constrainedWidth = constrainedHeight * originalAspectRatio;
            } else {
              // Height too large relative to width – recompute height
              constrainedHeight = constrainedWidth / originalAspectRatio;
            }
          }

          newWidth = constrainedWidth;
          newHeight = constrainedHeight;

          // Re-centre so the resize is symmetric around the original centre.
          newX = originalCenter.x - newWidth / 2;
          newY = originalCenter.y - newHeight / 2;
        } else if (maintainAspectRatio) {
          if (isCorner) {
            // For corners, determine which dimension to adjust to maintain aspect ratio
            const currentRatio = newWidth / newHeight;
            if (currentRatio > originalAspectRatio) {
              // Too wide, adjust height
              newHeight = newWidth / originalAspectRatio;
              if (isNorth) {
                newY = originalBounds.y + originalBounds.height - newHeight;
              }
            } else {
              // Too tall, adjust width
              newWidth = newHeight * originalAspectRatio;
              if (isWest) {
                newX = originalBounds.x + originalBounds.width - newWidth;
              }
            }
          } else if (isHorizontalEdge) {
            // For horizontal edges, adjust height to maintain aspect ratio
            newHeight = newWidth / originalAspectRatio;
            newY = originalBounds.y + (originalBounds.height - newHeight) / 2;
          } else if (isVerticalEdge) {
            // For vertical edges, adjust width to maintain aspect ratio
            newWidth = newHeight * originalAspectRatio;
            newX = originalBounds.x + (originalBounds.width - newWidth) / 2;
          }
        } else if (symmetricalResize) {
          // For symmetrical resize, calculate growth and apply symmetrically
          if (isEast) {
            const rightGrowth =
              newX + newWidth - (originalBounds.x + originalBounds.width);
            newWidth = newWidth + rightGrowth;
          } else if (isWest) {
            const leftGrowth = originalBounds.x - newX;
            newWidth = newWidth + leftGrowth;
          }

          if (isSouth) {
            const bottomGrowth =
              newY + newHeight - (originalBounds.y + originalBounds.height);
            newHeight = newHeight + bottomGrowth;
          } else if (isNorth) {
            const topGrowth = originalBounds.y - newY;
            newHeight = newHeight + topGrowth;
          }

          // Center the result
          newX = originalCenter.x - newWidth / 2;
          newY = originalCenter.y - newHeight / 2;
        }

        // Apply size constraints
        const constrainedWidth = clamp(newWidth, minWidth, maxWidth);
        const constrainedHeight = clamp(newHeight, minHeight, maxHeight);

        // Adjust position if constraints were applied
        if (constrainedWidth !== newWidth) {
          if (symmetricalResize) {
            newX = getCenter(originalBounds).x - constrainedWidth / 2;
          } else if (isWest) {
            newX = originalBounds.x + originalBounds.width - constrainedWidth;
          }
        }

        if (constrainedHeight !== newHeight) {
          if (symmetricalResize) {
            newY = getCenter(originalBounds).y - constrainedHeight / 2;
          } else if (isNorth) {
            newY = originalBounds.y + originalBounds.height - constrainedHeight;
          }
        }

        return {
          x: canMove ? Math.round(newX) : originalBounds.x,
          y: canMove ? Math.round(newY) : originalBounds.y,
          width: Math.round(constrainedWidth),
          height: Math.round(constrainedHeight),
        };
      },
      [minWidth, minHeight, maxWidth, maxHeight],
    );

    const endResize = useCallback(() => {
      elementsResizingRef.current?.forEach((element) => {
        if ((element as CanvasItemRef)?.onResizeEnd) {
          (element as CanvasItemRef).onResizeEnd?.();
          return;
        }

        const elementHTMLElement = element as HTMLElement;
        const originalWidth = elementHTMLElement.getAttribute(
          "data-original-width",
        );
        const originalHeight = elementHTMLElement.getAttribute(
          "data-original-height",
        );
        if (originalWidth !== null) {
          elementHTMLElement.style.width = originalWidth || "";
        }
        if (originalHeight !== null) {
          elementHTMLElement.style.height = originalHeight || "";
        }

        elementHTMLElement.removeAttribute("data-original-width");
        elementHTMLElement.removeAttribute("data-original-height");
      });

      resizeStateRef.current = {
        isResizing: false,
        handleType: null,
        originalBounds: { x: 0, y: 0, width: 0, height: 0 },
        startMouseX: 0,
        startMouseY: 0,
        originalAspectRatio: 1,
      };

      onResizeEnd?.(appliedItemRectRef.current);
      lockedSizeRef.current = false;
    }, [elementsResizingRef, onResizeEnd]);

    // Schedule update if not already scheduled
    const scheduleUpdate = useCallback(() => {
      if (updateRafId.current) return; // Already scheduled

      updateRafId.current = requestAnimationFrame(applyPendingUpdates);
    }, [applyPendingUpdates]);

    // ================================================================================
    // MOUSE HANDLERS
    // ================================================================================

    const handleMouseDown = useCallback(
      (e: React.MouseEvent, handleType: ResizeHandleType) => {
        e.preventDefault();
        e.stopPropagation();

        const canvasCoords = clientToCanvas
          ? clientToCanvas(e.clientX, e.clientY)
          : null;

        const currentItem = appliedItemRectRef.current;
        resizeStateRef.current = {
          isResizing: true,
          handleType,
          originalBounds: {
            x: currentItem.x,
            y: currentItem.y,
            width: currentItem.width,
            height: currentItem.height,
          },
          startMouseX: canvasCoords ? canvasCoords?.x : e.clientX,
          startMouseY: canvasCoords ? canvasCoords?.y : e.clientY,
          originalAspectRatio: currentItem.width / currentItem.height,
        };

        lockedSizeRef.current = true;
        onResizeStart?.();

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
      },
      [clientToCanvas],
    );

    const handleMouseMove = useCallback(
      (e: MouseEvent) => {
        const resizeState = resizeStateRef.current;
        if (!resizeState.isResizing || !resizeState.handleType) return;

        const canvasCoords = clientToCanvas
          ? clientToCanvas(e.clientX, e.clientY)
          : null;

        const deltaX =
          (canvasCoords ? canvasCoords?.x : e.clientX) -
          resizeState.startMouseX;
        const deltaY =
          (canvasCoords ? canvasCoords?.y : e.clientY) -
          resizeState.startMouseY;

        const newBounds = calculateNewBounds(
          resizeState.handleType,
          resizeState.originalBounds,
          deltaX,
          deltaY,
          e.shiftKey,
          resizeState.originalAspectRatio,
          e.altKey,
        );

        const updatedItem = { ...pendingUpdateItemRect.current, ...newBounds };
        pendingUpdateItemRect.current = updatedItem;

        // Immediate handle position update for responsiveness
        scheduleUpdate();

        // Batch the item change notifications with RAF to prevent render blocking
        if (!rafScheduled.current) {
          rafScheduled.current = true;
          resizeRafId.current = requestAnimationFrame(() => {
            onItemChange(updatedItem);
            rafScheduled.current = false;
          });
        }
      },
      [clientToCanvas, calculateNewBounds, scheduleUpdate, onItemChange],
    );

    const handleMouseUp = useCallback(() => {
      const resizeState = resizeStateRef.current;
      if (!resizeState.isResizing) return;

      // Cancel any pending RAF and do final update
      if (resizeRafId.current) {
        cancelAnimationFrame(resizeRafId.current);
        resizeRafId.current = null;
      }
      rafScheduled.current = false;

      onItemChange(pendingUpdateItemRect.current);
      endResize();

      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }, [handleMouseMove, onItemChange, endResize]);

    const startResize = useCallback(
      (e: React.MouseEvent, handleType: ResizeHandleType) => {
        handleMouseDown(e, handleType);
      },
      [handleMouseDown],
    );

    React.useImperativeHandle(
      ref,
      () => ({
        startResize,
        applyFullResize: (rect: Partial<ItemRect>) => {
          if (!resizeStateRef.current?.isResizing) {
            const currentItem = appliedItemRectRef.current;

            // So that the resizing works start it for the apply pending updates
            resizeStateRef.current = {
              isResizing: true,
              handleType:
                rect.width !== undefined
                  ? "e"
                  : rect.height !== undefined
                    ? "s"
                    : null,
              originalBounds: {
                x: currentItem.x,
                y: currentItem.y,
                width: currentItem.width,
                height: currentItem.height,
              },
              // Not needed
              startMouseX: 0,
              startMouseY: 0,

              originalAspectRatio: currentItem.width / currentItem.height,
            };
          }

          const fullRect = {
            ...pendingUpdateItemRect.current,
            ...rect,
          };
          pendingUpdateItemRect.current = fullRect;

          applyPendingUpdates();
          endResize();
        },
        applyInstantResize: (rect: Partial<ItemRect>) => {
          if (!resizeStateRef.current?.isResizing) {
            const currentItem = appliedItemRectRef.current;
            resizeStateRef.current = {
              isResizing: true,
              handleType:
                rect.width !== undefined
                  ? "e"
                  : rect.height !== undefined
                    ? "s"
                    : null,
              originalBounds: {
                x: currentItem.x,
                y: currentItem.y,
                width: currentItem.width,
                height: currentItem.height,
              },
              // Not needed
              startMouseX: 0,
              startMouseY: 0,

              originalAspectRatio: currentItem.width / currentItem.height,
            };
          }

          const updatedItem = { ...pendingUpdateItemRect.current, ...rect };
          pendingUpdateItemRect.current = updatedItem;

          // Immediate handle position update for responsiveness
          scheduleUpdate();

          // Batch the item change notifications with RAF to prevent render blocking
          if (!rafScheduled.current) {
            rafScheduled.current = true;
            resizeRafId.current = requestAnimationFrame(() => {
              onItemChange(updatedItem);
              rafScheduled.current = false;
            });
          }
        },
        instantUpdateWithRect: (rect: Partial<ItemRect>) => {
          const fullRect = {
            ...pendingUpdateItemRect.current,
            ...rect,
          };
          pendingUpdateItemRect.current = fullRect;
          applyPendingUpdates();
        },
        instantUpdateZIndex: (zIndex: number) => {
          if (outerDivRef.current) {
            outerDivRef.current.style.zIndex = zIndex.toString();
          }
        },
      }),
      [
        startResize,
        applyPendingUpdates,
        endResize,
        scheduleUpdate,
        onItemChange,
      ],
    );

    // Cleanup RAF on unmount
    useEffect(() => {
      return () => {
        if (updateRafId.current) {
          cancelAnimationFrame(updateRafId.current);
        }
      };
    }, []);

    // Update items when props change
    useEffect(() => {
      // If the user is currently editing an item's size, don't override it,
      // otherwise we can treat it as if it was resized there
      if (lockedSizeRef.current) {
        return;
      }

      if (
        itemRect.width !== appliedItemRectRef.current.width ||
        itemRect.height !== appliedItemRectRef.current.height ||
        itemRect.x !== appliedItemRectRef.current.x ||
        itemRect.y !== appliedItemRectRef.current.y
      ) {
        pendingUpdateItemRect.current = itemRect;
      }

      scheduleUpdate();

      // Also update CSS variables since dimensions changed (affects handle visibility)
      updateCssVariablesRef.current?.();
    }, [itemRect.width, itemRect.height, itemRect.x, itemRect.y]);

    // Cleanup RAF on unmount
    useEffect(() => {
      return () => {
        if (resizeRafId.current) {
          cancelAnimationFrame(resizeRafId.current);
        }
      };
    }, []);

    // ================================================================================
    // RENDER
    // ================================================================================

    const handles: ResizeHandleType[] = [
      "n",
      "e",
      "s",
      "w",
      "nw",
      "ne",
      "sw",
      "se",
    ];

    const renderHandleContent = useCallback((handleType: ResizeHandleType) => {
      const { isCorner } = getHandleDirections(handleType);

      if (isCorner) {
        // Corner handle - fixed pixel size white square
        // Uses CSS calc() with --unzoom-multiplier variable for smooth updates without re-renders
        // Uses --handle-display variable to hide when storyboard is too small
        return (
          <div
            className="bg-white border border-blue-500"
            style={{
              display: "var(--handle-display, block)",
              width: `calc(var(--unzoom-multiplier, 1) * ${CORNER_HANDLE_SIZE}px)`,
              height: `calc(var(--unzoom-multiplier, 1) * ${CORNER_HANDLE_SIZE}px)`,
              borderWidth: `calc(var(--unzoom-multiplier, 1) * 1px)`,
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        );
      }

      // Invisible edge handles for now
      return null;
    }, []);

    return (
      <div
        ref={outerDivRef}
        className={cn(
          "absolute inset-0 w-full h-full pointer-events-none",
          hidden && "hidden",
        )}
      >
        {/* Selection outline - underneath handles, fixed 2px border */}
        <ElementOutline
          type={droppingUI ? OutlineType.CHILD : OutlineType.PRIMARY}
          itemRect={itemRect}
          selected={true}
          tagName={itemRect.tagName}
          isComponent={itemRect.isComponent}
          ref={(el) => {
            if (el) {
              handleRefs.current.set("outline", el);
            } else {
              handleRefs.current.delete("outline");
            }
          }}
          iframeRef={iframeRef}
        />

        {/* Resize handles */}
        {!droppingUI &&
          handles.map((handleType) => {
            // Use ref for hit area bounds - these are invisible so slight lag during zoom is fine
            const { left, top, width, height } = getHandleBounds(
              handleType,
              unzoomMultiplierRef.current,
              itemRect,
            );

            return (
              <div
                key={handleType}
                className="absolute"
                style={{
                  width,
                  height,
                  left,
                  top,
                  cursor: cursors[handleType],
                  pointerEvents: notInteractable ? "none" : "auto",
                  zIndex: 10,
                }}
                ref={(el) => {
                  if (el) {
                    handleRefs.current.set(handleType, el);
                  } else {
                    handleRefs.current.delete(handleType);
                  }
                }}
                onMouseDown={(e) => handleMouseDown(e, handleType)}
              >
                {renderHandleContent(handleType)}
              </div>
            );
          })}
      </div>
    );
  },
);

export default ResizableOutline;
export type { ResizableOutlineRef, ResizableOutlineProps, ItemRect };
